import { useRef } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { TeamSubjectDetail, TeamSubjectPage, TeamSubjectQuery } from '@/types/monitoring';
import { toError } from '@/utils/errors';
import { UNCHANGED, usePoll, withEtag } from './usePoll';

/**
 * Alerts reach a client by email; these pages are where they come to read
 * one. A minute is plenty, and the steady state is a body-less 304.
 */
export const CLIENT_MONITORING_POLL_MS = 60_000;

const EMPTY_PAGE: TeamSubjectPage = { items: [], total: 0, version: '' };

/** The caller's team's monitored subjects. */
export function useClientMonitoredSubjects(query: TeamSubjectQuery) {
  const key = JSON.stringify(query);
  const queryRef = useRef(query);
  queryRef.current = query;

  const poll = usePoll<TeamSubjectPage>({
    interval: CLIENT_MONITORING_POLL_MS,
    refetchDeps: [key],
    queryFn: async ({ signal, etag, manual }) => {
      const result = await vnextApiClient.listClientMonitoredSubjects(queryRef.current, {
        signal,
        etag: manual ? null : etag,
      });
      return result === 'not-modified' ? UNCHANGED : withEtag(result.data, result.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });

  return {
    page: poll.data ?? EMPTY_PAGE,
    isLoading: poll.isLoading,
    error: poll.error === undefined ? null : toError(poll.error),
  };
}

/**
 * One monitored subject and a page of the findings its team was sent.
 *
 * `error` carries the server's message, which is what the page shows: a
 * missing subject (including another team's) and a role that may not see
 * monitoring each come back as a readable sentence.
 */
export function useClientMonitoredSubject(
  subjectId: string | undefined,
  query: { page?: number; pageSize?: number }
) {
  const key = JSON.stringify([subjectId, query]);
  const queryRef = useRef(query);
  queryRef.current = query;

  const poll = usePoll<TeamSubjectDetail>({
    enabled: Boolean(subjectId),
    interval: CLIENT_MONITORING_POLL_MS,
    refetchDeps: [key],
    queryFn: async ({ signal, etag, manual }) => {
      const result = await vnextApiClient.getClientMonitoredSubject(
        subjectId as string,
        queryRef.current,
        { signal, etag: manual ? null : etag }
      );
      return result === 'not-modified' ? UNCHANGED : withEtag(result.data, result.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });

  return {
    detail: poll.data ?? null,
    isLoading: poll.isLoading,
    error: poll.error === undefined ? null : toError(poll.error),
  };
}
